import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { SimplePool } from 'nostr-tools/pool'
import { RELAYS } from '../utils/constants.js'

// Provides a SimplePool, plus a convenience subscribe()/publish() pair.
//
// Important: the pool is reused across StrictMode's double-mount. We do NOT
// close or null it in the cleanup — if we did, the second mount would see
// a null pool and every subscribe()/publish() call would silently no-op.
// The browser reclaims the underlying WebSockets when the page unloads.
export function useNostr() {
  const poolRef = useRef(null)
  if (!poolRef.current) {
    poolRef.current = new SimplePool()
  }

  const [connected, setConnected] = useState(false)

  useEffect(() => {
    let cancelled = false
    const pool = poolRef.current
    if (!pool) return

    Promise.allSettled(
      RELAYS.map((url) => pool.ensureRelay(url))
    ).then((results) => {
      if (!cancelled) {
        const anyOpen = results.some((r) => r.status === 'fulfilled')
        setConnected(anyOpen)
      }
    })

    return () => {
      cancelled = true
      // Intentionally not closing or nulling the pool here — see header.
    }
  }, [])

  const api = useMemo(() => {
    const subscribe = (filterOrFilters, onEvent, onEose) => {
      const pool = poolRef.current
      if (!pool) {
        console.warn('[Verdant] subscribe called before pool was initialized')
        return { close: () => {} }
      }
      // nostr-tools 2.x SimplePool.subscribeMany expects a single filter
      // object; passing an array gets it nested and the relay sees a
      // malformed REQ. Accept either form here for caller convenience.
      const filter = Array.isArray(filterOrFilters)
        ? filterOrFilters[0]
        : filterOrFilters
      const sub = pool.subscribeMany(RELAYS, filter, {
        onevent: (ev) => {
          try { onEvent?.(ev) } catch (e) { console.warn(e) }
        },
        oneose: () => onEose?.(),
      })
      return sub
    }

    const publish = async (signed) => {
      const pool = poolRef.current
      if (!pool) throw new Error('Pool not initialized')
      const promises = pool.publish(RELAYS, signed)
      // Resolve when at least one relay accepts; surface failures.
      const settled = await Promise.allSettled(promises)
      const perRelay = settled.map((s, i) => ({
        relay: RELAYS[i],
        accepted: s.status === 'fulfilled',
        reason: s.status === 'rejected' ? (s.reason?.message || 'unknown') : null,
      }))
      reportRelayHealth(signed, perRelay)
      const ok = perRelay.filter((r) => r.accepted).length
      if (ok === 0) {
        const reasons = perRelay.map((r) => r.reason || 'unknown').join('; ')
        const err = new Error(`No relays accepted the event: ${reasons}`)
        Sentry.withScope((s) => {
          s.setTag('op', 'nostr_publish')
          s.setExtra('relayCount', perRelay.length)
          s.setExtra('reasons', reasons)
          s.setExtra('eventKind', signed?.kind)
          Sentry.captureException(err)
        })
        throw err
      }
      return { accepted: ok, total: perRelay.length }
    }

    return {
      get pool() { return poolRef.current },
      relays: RELAYS,
      subscribe,
      publish,
    }
  }, [])

  return { ...api, connected }
}

// Fire-and-forget POST to /api/relay-health — Vercel function logs
// per-relay accept/reject for monitoring. keepalive lets the request
// finish even if the user navigates away mid-publish.
function reportRelayHealth(signed, perRelay) {
  try {
    fetch('/api/relay-health', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        event_id: signed?.id || null,
        kind: signed?.kind ?? null,
        relays: perRelay,
      }),
      keepalive: true,
    }).catch(() => { /* fire and forget */ })
  } catch { /* noop */ }
}
